<script setup lang="ts">
import { computed } from 'vue';
import { useScrollTo } from '@shared/lib';
import { useAppearance, useInstitutionStore } from '@entities/institution';

const storeInstitution = useInstitutionStore();
const { scrollTo } = useScrollTo();
const { catalog: catalogAppearance } = useAppearance();

const route = useRoute();
const router = useRouter();

/**
 * В двухуровневом каталоге боковое меню остаётся — это и есть «быстрая навигация»
 * между категориями. Но ведёт оно уже не к якорю на общей странице, а внутрь
 * категории: якорей других категорий на экране просто нет.
 */
const isTwoLevel = computed(() => catalogAppearance.value.nesting === 'two-level');
const openedSlug = computed(() => (typeof route.query.c === 'string' ? route.query.c : ''));

const handleCategoryClick = (to?: string) => {
  if (!to) return;
  if (isTwoLevel.value) {
    router.push({ query: { ...route.query, c: to } });
    return;
  }
  scrollTo(to);
};

/**
 * Адрес категории для href.
 *
 * Раньше пункты меню были <button> — переход делал JS, а в разметке не
 * оставалось ни одной ссылки на категории. Поисковик по такой навигации не
 * ходит: он видит главную как один документ без внутренней структуры.
 *
 * Клик по-прежнему обрабатываем сами (preventDefault) — поведение не меняется,
 * но у ссылки появляется настоящий href: его видит и робот, и пользователь,
 * который открывает пункт в новой вкладке.
 */
const categoryHref = (to?: string) => {
  if (!to) return;
  return isTwoLevel.value ? `/?c=${encodeURIComponent(to)}` : `#${to}`;
};

const onCategoryClick = (event: MouseEvent, to?: string) => {
  // Ctrl/Cmd-клик и средняя кнопка — пусть браузер откроет в новой вкладке сам.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  event.preventDefault();
  handleCategoryClick(to);
};
</script>

<template>
  <div class="aside-menu">
    <div class="aside-menu__sticky">
      <!-- Без v-if: видимость и так на CSS (.aside-menu — max-2xl:hidden), а JS-гейт
           по ширине выкидывал навигацию из SSR-разметки (на сервере width = 0).
           Вместе с ней из HTML пропадали все ссылки на категории меню. -->
      <nav class="aside-menu__nav aside-menu-nav">
        <a
          v-for="(category, index) in storeInstitution.products"
          :key="index"
          :href="categoryHref(category.to)"
          class="aside-menu-nav__item"
          :class="{ 'aside-menu-nav__item--active': isTwoLevel && openedSlug === category.to }"
          @click="onCategoryClick($event, category.to)"
        >
          <!-- Условие по url, а не по img: у категории без картинки объект приходит
               с пустыми полями, и получался img с пустым src — такой адрес браузер
               разрешает в текущую страницу и качает её второй раз. -->
          <img
            v-if="category.img?.url"
            width="50"
            :src="category.img.url"
            :alt="category.category"
            loading="lazy"
            decoding="async"
          />
          <span>{{ category.category }}</span>
        </a>
      </nav>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .aside-menu {
    @apply max-2xl:hidden max-xl:pt-16 ;

    &__sticky {
      @apply xl:sticky xl:top-24 max-lg:mt-4 pt-7;
    }

    &-nav{
      @apply grid gap-5 xl:max-h-[calc(100vh-8rem)] xl:overflow-y-auto xl:pr-2;
      scrollbar-gutter: stable;

      &__item{
        @apply flex items-center justify-start gap-3;

        // Во втором уровне подсвечиваем, где гость находится: без этого боковое
        // меню перестаёт отвечать на вопрос «в какой я категории».
        &--active {
          @apply font-semibold;
          color: var(--app-accent, #ef4444);
        }
      }
    }
  }
</style>
